import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import { Box, ButtonBase, CircularProgress, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { trackClick } from "@/components/_helpers";
import { NICKNAME_MAX } from "@/utils/arcade/leaderboard";
import { freeRunsLabel } from "@/utils/arcade/playGate";
import { BADGES, playedToday, streakLabel } from "@/utils/arcade/progress";
import { utcDateKey } from "@/utils/arcade/dailyChallenge";
import { arcadePath } from "@/utils/arcade/lineup";
import type { ArcadeTheme } from "@/utils/arcade/theme";
import { useArcadeQuery, type BoardEntry } from "./arcadeBackend";
import { BadgeIcon } from "./BadgeIcon";
import type { ArcadeRetention } from "./useArcadeRetention";

type Tab = "challenge" | "today" | "all";

const SHOWN = 5;

// The hall of fame on /games (HIGH_SCORES_ID in HighScoresSection, not
// imported so the game chunk does not carry the whole section).
const HALL_OF_FAME = `${arcadePath()}#high-scores`;

const TAB_LABELS: Record<Tab, string> = { challenge: "Challenge", today: "Today", all: "All time" };

// The leaderboard half of the game over card: the song and game's board and
// the one action that matters next (post, save or unlock). Sized to sit in a
// phone's game card without pushing the buttons off the stage.
export function GameOverBoard({ retention, theme, score }: { retention: ArcadeRetention; theme: ArcadeTheme; score: number }) {
  const { access, postState, challenge, songSlug, game } = retention;
  const palette = theme.palette;
  const [tab, setTab] = useState<Tab>(challenge ? "challenge" : "today");
  const [editing, setEditing] = useState(!retention.nickname);
  const [name, setName] = useState(retention.nickname);

  useEffect(() => {
    setName(retention.nickname);
    setEditing(!retention.nickname);
  }, [retention.nickname]);

  const boardArgs = { songSlug, game, period: tab === "all" ? ("all" as const) : ("today" as const), ...(tab === "challenge" && challenge ? { challenge } : {}) };
  const board = useArcadeQuery("board", boardArgs);
  const identity = access.reason === "member" || access.reason === "admin" ? {} : { email: access.storedEmail ?? undefined, unlockToken: access.unlockToken ?? undefined };
  const mine = useArcadeQuery("mine", access.canPostScores ? { ...boardArgs, ...identity } : "skip");

  if (access.reason === "offline") return null;

  const entries: BoardEntry[] = board.status === "ready" ? board.value.entries : [];
  const me = mine.status === "ready" ? mine.value : null;
  const myIndex = me ? entries.findIndex((entry) => entry.nickname === me.nickname && entry.score === me.score) : -1;
  const shown = entries.slice(0, SHOWN);
  const showMeBelow = Boolean(me) && (myIndex < 0 || myIndex >= SHOWN);
  const tabs: Tab[] = challenge ? ["challenge", "today", "all"] : ["today", "all"];
  const text = { fontFamily: "Poppins, sans-serif", color: palette.text, lineHeight: 1.25 } as const;
  const today = utcDateKey(Date.now());
  const hasScore = score > 0;

  const row = (entry: { rank: number; nickname: string; score: number }, mine: boolean, key: string) => (
    <Box
      key={key}
      component="li"
      sx={{
        display: "grid",
        gridTemplateColumns: "26px minmax(0, 1fr) auto",
        alignItems: "center",
        gap: "8px",
        minHeight: { xs: "21px", md: "24px" },
        px: "8px",
        borderRadius: "8px",
        backgroundColor: mine ? `color-mix(in srgb, ${palette.primary} 26%, transparent)` : "transparent",
      }}
    >
      <Typography component="span" sx={{ ...text, fontSize: "12px", fontWeight: 700, color: entry.rank <= 3 ? palette.accent : palette.text, opacity: entry.rank <= 3 ? 1 : 0.7, fontVariantNumeric: "tabular-nums" }}>
        {entry.rank}
      </Typography>
      <Typography component="span" sx={{ ...text, fontSize: "13px", fontWeight: mine ? 700 : 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textAlign: "left" }}>
        {entry.nickname}
        {mine ? " (you)" : ""}
      </Typography>
      <Typography component="span" sx={{ ...text, fontSize: "13px", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
        {entry.score.toLocaleString()}
      </Typography>
    </Box>
  );

  const pill = {
    minHeight: "44px",
    px: "18px",
    borderRadius: "999px",
    gap: "8px",
    fontFamily: "Poppins, sans-serif",
    fontWeight: 700,
    fontSize: "14px",
    color: palette.backgroundDeep,
    backgroundColor: palette.text,
    "&:hover": { backgroundColor: palette.accent },
    "&:focus-visible": { outline: `2px solid ${palette.accent}`, outlineOffset: "2px" },
    "&.Mui-disabled": { opacity: 0.6 },
  } as const;

  let action: React.ReactNode = null;
  if (!hasScore) {
    action = null;
  } else if (postState.status === "posted") {
    const result = postState.result;
    action = (
      <Typography role="status" sx={{ ...text, fontSize: "14px", fontWeight: 600 }}>
        <EmojiEventsIcon sx={{ fontSize: 18, color: palette.accent, verticalAlign: "-3px", mr: "6px" }} />
        {result.challengeRank !== null ? `Challenge #${result.challengeRank}, today` : "Today"} #{result.todayRank}, all time #{result.rank}
        {result.rankCapped ? "+" : ""}
      </Typography>
    );
  } else if (postState.status === "review") {
    action = (
      <Typography role="status" sx={{ ...text, fontSize: "13px", opacity: 0.9 }}>
        <EmojiEventsIcon sx={{ fontSize: 18, color: palette.accent, verticalAlign: "-3px", mr: "6px" }} />
        {postState.message}
      </Typography>
    );
  } else if (postState.status === "held") {
    action = (
      <Typography role="status" sx={{ ...text, fontSize: "13px", opacity: 0.9 }}>
        Saved. Confirm the email we sent{access.storedEmail ? ` to ${access.storedEmail}` : ""} and it goes on the board.
      </Typography>
    );
  } else if (editing) {
    action = (
      <Box
        component="form"
        onSubmit={(event: React.FormEvent) => {
          event.preventDefault();
          void retention.post(name);
        }}
        sx={{ display: "flex", gap: "8px", width: "100%" }}
      >
        <Box
          component="input"
          value={name}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
          onKeyDown={(event: React.KeyboardEvent) => event.stopPropagation()}
          maxLength={NICKNAME_MAX}
          placeholder="Your name on the board"
          aria-label="Your name on the board"
          autoComplete="nickname"
          sx={{
            flex: 1,
            minWidth: 0,
            width: 0,
            height: "44px",
            px: "14px",
            borderRadius: "999px",
            border: `1px solid color-mix(in srgb, ${palette.text} 30%, transparent)`,
            backgroundColor: "rgba(255,255,255,0.08)",
            color: palette.text,
            fontFamily: "Poppins, sans-serif",
            fontSize: "16px",
            outline: "none",
            "&:focus-visible": { borderColor: palette.accent },
            "&::placeholder": { color: palette.text, opacity: 0.5 },
          }}
        />
        <ButtonBase type="submit" disabled={postState.status === "posting"} sx={pill}>
          {postState.status === "posting" ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : access.canPostScores ? "Post" : "Save"}
        </ButtonBase>
      </Box>
    );
  } else {
    action = (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", columnGap: "12px", flexWrap: "wrap" }}>
        <ButtonBase onClick={() => void retention.post(retention.nickname)} disabled={postState.status === "posting"} sx={pill}>
          {postState.status === "posting" ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <EmojiEventsIcon sx={{ fontSize: 18 }} />}
          {access.canPostScores ? `Post as ${retention.nickname}` : "Save this score"}
        </ButtonBase>
        <ButtonBase onClick={() => setEditing(true)} sx={{ ...text, fontSize: "13px", minHeight: "44px", textDecoration: "underline", textUnderlineOffset: "3px", opacity: 0.8 }}>
          Change name
        </ButtonBase>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", display: "grid", gap: { xs: "6px", md: "8px" } }}>
      <Box sx={{ borderRadius: "14px", backgroundColor: "rgba(255,255,255,0.06)", p: "6px", display: "grid", gap: "4px" }}>
        <Box role="tablist" aria-label="Leaderboard" sx={{ display: "flex", gap: "4px" }}>
          {tabs.map((option) => (
            <ButtonBase
              key={option}
              role="tab"
              aria-selected={tab === option}
              onClick={() => setTab(option)}
              sx={{
                flex: 1,
                minHeight: { xs: "28px", md: "30px" },
                borderRadius: "9px",
                fontFamily: "Poppins, sans-serif",
                fontSize: "12px",
                fontWeight: 700,
                letterSpacing: "0.04em",
                color: tab === option ? palette.backgroundDeep : palette.text,
                backgroundColor: tab === option ? palette.accent : "transparent",
                opacity: tab === option ? 1 : 0.75,
              }}
            >
              {TAB_LABELS[option]}
            </ButtonBase>
          ))}
        </Box>
        <Box component="ol" aria-label={`${TAB_LABELS[tab]} leaderboard`} sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: "1px", minHeight: "72px" }}>
          {board.status === "loading" ? (
            <Typography component="li" sx={{ ...text, fontSize: "13px", opacity: 0.6, py: "8px" }}>Loading the board...</Typography>
          ) : board.status === "error" ? (
            <Typography component="li" sx={{ ...text, fontSize: "13px", opacity: 0.6, py: "8px" }}>The board is not available right now.</Typography>
          ) : shown.length === 0 ? (
            <Typography component="li" sx={{ ...text, fontSize: "13px", opacity: 0.75, py: "8px" }}>
              {tab === "all" ? "No scores yet. Be the first on the board." : "Nobody has posted today. Take the top spot."}
            </Typography>
          ) : (
            shown.map((entry, index) => row(entry, index === myIndex, `${entry.rank}-${entry.nickname}-${index}`))
          )}
          {showMeBelow && me ? row({ rank: me.rank, nickname: me.nickname, score: me.score }, true, "me") : null}
        </Box>
        <Box
          component={RouterLink}
          to={HALL_OF_FAME}
          onClick={() => trackClick("arcade:over-halloffame", { label: "Game over hall of fame", section: "arcade", href: HALL_OF_FAME })}
          sx={{
            justifySelf: "end",
            px: "8px",
            lineHeight: "18px",
            fontFamily: "Poppins, sans-serif",
            fontSize: "12px",
            fontWeight: 600,
            color: palette.text,
            opacity: 0.75,
            textDecoration: "underline",
            textUnderlineOffset: "3px",
            "&:hover": { opacity: 1, color: palette.accent },
            "&:focus-visible": { opacity: 1, outline: `2px solid ${palette.accent}`, outlineOffset: "2px", borderRadius: "4px" },
          }}
        >
          Hall of fame
        </Box>
      </Box>
      {action}
      {!access.unlocked && retention.freeLeft !== null && hasScore && postState.status !== "posted" ? (
        <Typography sx={{ ...text, fontSize: "12px", opacity: 0.75 }}>
          {freeRunsLabel(retention.freeLeft)}. Unlock for unlimited plays.
        </Typography>
      ) : null}
      {retention.badgeToast.length > 0 ? (
        <Typography role="status" sx={{ ...text, fontSize: "12px", fontWeight: 700, color: palette.accent, display: { xs: "inline-flex", md: "none" }, alignItems: "center", justifyContent: "center", gap: "5px" }}>
          <BadgeIcon id={retention.badgeToast[0]} size={16} />
          Badge earned: {BADGES[retention.badgeToast[0]].name}
          {retention.badgeToast.length > 1 ? ` and ${retention.badgeToast.length - 1} more` : ""}
        </Typography>
      ) : null}
      {retention.streak > 0 && playedToday(retention.progress, today) ? (
        <Typography sx={{ ...text, fontSize: "12px", opacity: 0.8, display: { xs: retention.badgeToast.length > 0 ? "none" : "inline-flex", md: "inline-flex" }, alignItems: "center", justifyContent: "center", gap: "4px" }}>
          <LocalFireDepartmentIcon sx={{ fontSize: 16, color: palette.accent }} />
          {streakLabel(retention.streak)}. Come back tomorrow to keep it.
        </Typography>
      ) : null}
      {postState.status === "error" ? (
        <Typography role="alert" sx={{ ...text, fontSize: "13px", color: palette.danger }}>
          {postState.message}
        </Typography>
      ) : null}
    </Box>
  );
}

export default GameOverBoard;
